import React, { useEffect, useRef, useState } from 'react';
import { FiCheck, FiGlobe, FiRefreshCw } from 'react-icons/fi';
import { toast } from '../../../Toast';
import { useHubSite } from '../../shared';
import { ThumbArt } from './PictureCard';
import { getLinkSample, LinkSample, LinkType } from './newsSamples';

export type Opens = 'tab' | 'site';

// props every link form gets from AddNews: the title (the page's slug comes from it), and where to report
// "Picture taken from the link" once the details are in. sample = /edit opens with the spec's filled-in state.
export interface LinkFormProps {
    title: string;
    onTitle: (v: string) => void;
    onPictureFrom: (sub: string | null) => void;
    sample: boolean;
}

// the state behind Paste the link / When an investor clicks it, shared by the four link types
export const useLinkForm = (type: LinkType, { onTitle, onPictureFrom, sample }: LinkFormProps, opens: Opens) => {
    const { short } = useHubSite();
    const [s] = useState<LinkSample>(() => getLinkSample(type, short));
    const [url, setUrl] = useState(sample ? s.url : '');
    const [loading, setLoading] = useState(false);
    const [found, setFound] = useState(sample);
    const [fields, setFields] = useState<[string, string, string]>(sample ? s.fields : ['', '', '']);
    const [description, setDescription] = useState(sample ? s.description : '');
    const [quote, setQuote] = useState(sample ? s.quote ?? '' : '');
    const [click, setClick] = useState<Opens>(opens);
    const timer = useRef<number>();

    useEffect(() => () => window.clearTimeout(timer.current), []);

    const setField = (i: 0 | 1 | 2, v: string) => setFields(f => f.map((x, j) => j === i ? v : x) as [string, string, string]);

    // pretend to read the page behind the link: a short wait, then the sample's title, picture and date
    const getDetails = () => {
        if (!url.trim()) { toast.error('Paste a link first'); return; }
        setLoading(true);
        timer.current = window.setTimeout(() => {
            setLoading(false);
            setFound(true);
            onTitle(s.title);
            setFields(s.fields);
            setDescription(s.description);
            setQuote(s.quote ?? '');
            onPictureFrom(s.pictureFrom);
        }, 900);
    };

    return { s, url, setUrl, loading, found, getDetails, fields, setField, description, setDescription, quote, setQuote, click, setClick };
};

// "Paste the link" card: URL + Get Details, the green "Details found" box, then the title and the type's own fields (children)
export const PasteLinkCard: React.FC<{ icon: React.ReactNode; form: ReturnType<typeof useLinkForm>; placeholder: string }> = ({ icon, form, placeholder, children }) =>
    <section className="card hub-news-form">
        <div className="section-title"><span className="icon-tile">{icon}</span><h2>Paste the link</h2></div>
        <div className="field">
            <label className="label" htmlFor="hub-news-url">Link (URL)</label>
            <div className="hub-url-row">
                <input id="hub-news-url" value={form.url} placeholder={placeholder} onChange={e => form.setUrl(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') form.getDetails(); }} />
                <button type="button" className="btn-outline with-icon" disabled={form.loading} onClick={form.getDetails}>
                    <FiRefreshCw className={form.loading ? 'spin' : ''} /> {form.loading ? 'Getting…' : 'Get Details'}
                </button>
            </div>
        </div>
        {form.found && <div className="hub-found">
            <ThumbArt />
            <div className="hub-found-t">
                <strong><FiCheck /> Details found — check them below and change anything you like</strong>
                <span>{form.s.found}</span>
            </div>
        </div>}
        {children}
    </section>;

// "When an investor clicks it": open the link in a new tab, or play it on the client's site
export const ClickCard: React.FC<{ form: ReturnType<typeof useLinkForm>; newTab: string }> = ({ form, newTab }) => {
    const { host } = useHubSite();
    const choices: { value: Opens; title: string; hint: string }[] = [
        { value: 'tab', title: newTab, hint: 'Best for podcasts, news stories and webinar registration' },
        { value: 'site', title: 'Play it on our website', hint: `Works for YouTube, Vimeo, Spotify and Apple Podcasts — investors stay on ${host}` },
    ];
    return <section className="card hub-news-form">
        <div className="section-title"><span className="icon-tile"><FiGlobe /></span><h2>When an investor clicks it</h2></div>
        <div className="options" role="radiogroup" aria-label="When an investor clicks it">
            {choices.map(c => <button key={c.value} type="button" role="radio" aria-checked={form.click === c.value}
                className={`option${form.click === c.value ? ' selected' : ''}`} onClick={() => form.setClick(c.value)}>
                <span className="radio" />
                <span className="text"><strong>{c.title}</strong><span>{c.hint}</span></span>
            </button>)}
        </div>
    </section>;
};

// Title shown on the website — the same first field on every link type
export const TitleField: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) =>
    <label className="field">
        <span className="label">Title shown on the website</span>
        <input value={value} onChange={e => onChange(e.target.value)} />
    </label>;

// one of the three fields in a row; hint is the grey "· shown under the title" / "(optional)" after the label
export const SmallField: React.FC<{ label: string; hint?: string; value: string; onChange: (v: string) => void }> = ({ label, hint, value, onChange }) =>
    <label className="field">
        <span className="label">{label}{hint && <em>{hint}</em>}</span>
        <input value={value} onChange={e => onChange(e.target.value)} />
    </label>;

export const DescriptionField: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) =>
    <label className="field">
        <span className="label">Short description<em>· 1–2 sentences, shown in the list</em></span>
        <textarea value={value} rows={2} onChange={e => onChange(e.target.value)} />
    </label>;
